import React, { useState } from "react";
import axios from "axios";
import SectionTitle from "../../../assets/components/TitleGroup";
import { useFixedLayout } from "../../../hooks/useFixedLayout";
import ListCard from "../../../assets/components/ListCard";
import infoRed from "../../../assets/icons/warning/info_red.svg";
import PlaceholderB from "../../../assets/components/PlaceholderB";
import { SingleButton } from "../../../assets/components/ButtonGroup";

const AdsRegistSelf6 = ({
  shop,
  setStoreBusinessNumber,
  setStep8Shop,
  goToStep,
  targetStep8,
  targetStep4,
}) => {
  const [businessOwnerName, setBusinessOwnerName] = useState("");
  const [businessNumber, setBusinessNumber] = useState("");
  const [loading, setLoading] = useState(false);

  const { bottomSpace, footerRef } = useFixedLayout();
  const isDisabled = !businessOwnerName || !businessNumber;

  const formattedAddress = [
    shop?.zonecode ? `(${shop.zonecode})` : null,
    shop?.ROAD_NAME_ADDRESS || "",
    shop?.FLOOR_INFO ? `${shop.FLOOR_INFO}층` : null,
  ]
    .filter(Boolean)
    .join(" ");

  const categoryText = [
    shop?.LARGE_CATEGORY_NAME,
    shop?.MEDIUM_CATEGORY_NAME,
    shop?.SMALL_CATEGORY_NAME,
  ]
    .filter(Boolean)
    .join(" > ");

  const duplicateMessage = [
    "이미 등록된 매장입니다.",
    "매장명과 주소를 다시 확인해 주세요.",
    "매장당 한 개의 아이디만 등록 가능합니다.",
  ].join("\n");

  const isDuplicateError = (error, data) => {
    if (error?.response?.status === 409) return true;

    const message =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      data?.detail ||
      data?.message ||
      "";

    if (/1062|duplicate|중복/i.test(String(message))) return true;

    return (
      data?.available === false &&
      /duplicate|중복/i.test(String(data?.reason || data?.message || ""))
    );
  };

  const onSubmit = async () => {
    if (!businessOwnerName || !businessNumber) {
      window.alert("필수 정보를 모두 입력해 주세요.");
      return;
    }
    if (loading) return;

    setLoading(true);

    try {
      const payload = {
        user_id: Number(localStorage.getItem("user_id")),
        business_name: businessOwnerName,
        business_number: businessNumber,
        register_tag: shop?.REPRESENTATIVE_MENU || "",
        store_business_number: shop?.STORE_BUSINESS_NUMBER,
        road_name: shop?.ROAD_NAME_ADDRESS || "",
        status: "active",
      };

      const res = await axios.post(
        `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/store/register`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      if (res?.data === true || res?.data?.available === true) {
        try {
          const nextTag = String(shop?.REPRESENTATIVE_MENU || "").trim();
          if (nextTag) localStorage.setItem("registerTag", nextTag);
          else localStorage.removeItem("registerTag");
        } catch {
          // ignore
        }
        setStoreBusinessNumber(shop?.STORE_BUSINESS_NUMBER);
        setStep8Shop({
          storeName: shop?.STORE_NAME,
          address: formattedAddress,
          businessName: businessOwnerName,
          businessNumber,
          category: categoryText,
        });
        if (typeof targetStep8 === "number") goToStep(targetStep8);
        return;
      }

      if (res?.data?.available === false || isDuplicateError(null, res?.data)) {
        window.confirm(duplicateMessage);
        return;
      }

      if (typeof targetStep4 === "number") goToStep(targetStep4);
    } catch (error) {
      console.error("매장 정보 등록 오류:", error);

      if (isDuplicateError(error)) {
        window.confirm(duplicateMessage);
        if (typeof targetStep4 === "number") goToStep(targetStep4);
      } else {
        window.alert(`매장 정보 등록 중 오류:\n${error?.response?.data?.detail || error.message}`);
        if (typeof targetStep4 === "number") goToStep(targetStep4);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatBizNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 px-[16px] pt-[16px] flex flex-col" style={{ paddingBottom: bottomSpace }}>
        <SectionTitle
          title="매장 정보 등록"
          subTitle="추가 정보를 입력해 주세요"
          className="pb-[40px]"
        />

        <div className="pb-[36px]">
          <ListCard title={shop?.STORE_NAME} subtitle={formattedAddress} />

          <div className="pt-[24px] space-y-[12px]">
            <PlaceholderB
              label="대표자 이름"
              mode="input"
              placeholder="실명을 입력해 주세요"
              required
              value={businessOwnerName}
              onChange={(e) => setBusinessOwnerName(e.target.value)}
              autoComplete="name"
              rightIconMode="clear"
            />

            <PlaceholderB
              label="사업자등록번호"
              mode="input"
              required
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={12}
              value={formatBizNumber(businessNumber)}
              onChange={(e) => {
                const onlyDigits = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                setBusinessNumber(onlyDigits);
              }}
              autoComplete="off"
              placeholder="예: 123-45-67890"
              rightIconMode="clear"
            />
          </div>
        </div>

        <div className="mt-auto text-c2 text-text-danger flex gap-[4px]">
          <img src={infoRed} alt="경고" className="w-[16px] h-[17px]" />
          <p>본인과 관련 없는 사업자를 등록하는 경우 이용 제한과 법적 제재를 받을 수 있습니다.</p>
        </div>
      </div>

      <div ref={footerRef} className="fixed bottom-0 left-0 right-0 bg-surface-default">
        <SingleButton onClick={onSubmit} disabled={loading || isDisabled}>
          {loading ? "처리 중..." : "등록하기"}
        </SingleButton>
      </div>
    </div>
  );
};

export default AdsRegistSelf6;
